import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactElement, type ReactNode } from 'react';

import { useHttpClient } from '@/app/providers';

import { badgeCount, type NavEntry } from '../navigation';

/**
 * Pastilles chiffrees de la barre laterale.
 *
 * Trois regles, toutes issues de l'ADR 0035 :
 *
 * 1. **Lues une fois**, au montage de la coque — qui reste montee d'une page a
 *    l'autre : changer d'ecran ne relit rien. Puis **sur demande** de l'ecran
 *    dont l'action change le nombre (« Rejouer » fait baisser les traitements en
 *    erreur), par `refresh`. **Jamais sur minuterie** : chaque lecture d'une
 *    route `/admin/*` ecrit une ligne d'audit (§4).
 * 2. **Un nombre rendu par le serveur, ou rien.** Une lecture en echec ou une
 *    reponse qui n'est pas un entier positif omet la pastille ; l'ancienne
 *    valeur n'est pas gardee non plus, elle ne serait plus vraie.
 * 3. Seules les entrees **rendues** sont lues — la coque passe celles que le
 *    role laisse voir : une pastille sans entree a afficher serait un appel pour
 *    rien, et un 403 de plus au journal d'audit. Quand le role arrive et qu'une
 *    entree apparait, seule celle-ci est lue ; les autres ne le sont pas deux fois.
 */
export interface NavBadges {
  readonly counts: Readonly<Record<string, number>>;
  /** Relit la pastille d'une entree, apres l'action qui la change. */
  readonly refresh: (entryId: string) => void;
}

const NavBadgesContext = createContext<NavBadges>({ counts: {}, refresh: () => undefined });

export const NavBadgesProvider = ({
  entries,
  children,
}: {
  readonly entries: readonly NavEntry[];
  readonly children: ReactNode;
}): ReactElement => {
  const http = useHttpClient();
  const [counts, setCounts] = useState<Readonly<Record<string, number>>>({});
  const mounted = useRef(true);
  /** Entrees deja lues au montage : une liste qui change ne relit que les nouvelles. */
  const readOnce = useRef(new Set<string>());

  const read = useCallback(
    async (entry: NavEntry): Promise<void> => {
      if (entry.badge === undefined || entry.target === undefined) {
        return;
      }

      let count: number | null;

      try {
        count = badgeCount(await entry.badge(http));
      } catch {
        count = null;
      }

      if (!mounted.current) {
        return;
      }

      setCounts((previous) => {
        const { [entry.id]: _previousCount, ...others } = previous;

        return count === null ? others : { ...others, [entry.id]: count };
      });
    },
    [http],
  );

  useEffect(() => {
    mounted.current = true;

    for (const entry of entries) {
      if (!readOnce.current.has(entry.id)) {
        readOnce.current.add(entry.id);
        void read(entry);
      }
    }

    return () => {
      mounted.current = false;
    };
  }, [entries, read]);

  const refresh = useCallback(
    (entryId: string): void => {
      const entry = entries.find((candidate) => candidate.id === entryId);

      if (entry !== undefined) {
        void read(entry);
      }
    },
    [entries, read],
  );

  const value = useMemo(() => ({ counts, refresh }), [counts, refresh]);

  return <NavBadgesContext.Provider value={value}>{children}</NavBadgesContext.Provider>;
};

export const useNavBadges = (): NavBadges => useContext(NavBadgesContext);
